import { useState, useMemo, useEffect } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import {
  echarts,
  baseChartOptions,
  USER_ROLE_COLORS,
  USER_ROLE_LABELS,
} from '../shared/echarts-config';
import { ChartContainer } from '../shared/ChartContainer';
import { EmptyState } from '../shared/EmptyState';
import { Button } from '@/shared/components/ui/button';
import type {
  ActiveUsersPeriod,
  LoginTrendPoint,
  DateRangePreset,
} from '../../../types/analytics.types';

type ViewMode = 'daily' | 'weekly';

type ActiveUsersBarChartProps = {
  weeklyData: ActiveUsersPeriod[];
  dailyData: LoginTrendPoint[];
  dateRange?: DateRangePreset;
};

const DAILY_DISABLED_RANGES: DateRangePreset[] = ['12m'];

const ROLE_KEYS = [
  'CARE_SEEKER',
  'FINANCIAL_ADVISOR',
  'SERVICE_PROVIDER',
  'ADMIN',
] as const;

const formatDailyLabel = (date: string): string => {
  const d = new Date(date);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
};

const buildChartOptions = (items: ActiveUsersPeriod[], mode: ViewMode) => ({
  ...baseChartOptions,
  legend: {
    data: ROLE_KEYS.map((key) => USER_ROLE_LABELS[key]),
    bottom: 0,
    textStyle: { fontSize: 12, color: '#6b7280' },
  },
  grid: { ...baseChartOptions.grid, bottom: '12%' },
  xAxis: {
    type: 'category' as const,
    data: items.map((p) =>
      mode === 'weekly' ? p.period : formatDailyLabel(p.period),
    ),
    axisLabel: { fontSize: 11, color: '#6b7280', rotate: mode === 'daily' ? 45 : 0 },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: ROLE_KEYS.map((role) => ({
    name: USER_ROLE_LABELS[role],
    type: 'bar' as const,
    stack: 'total',
    data: items.map((p) => p[role]),
    itemStyle: {
      color: USER_ROLE_COLORS[role],
      borderRadius: [0, 0, 0, 0],
    },
    barMaxWidth: mode === 'daily' ? 18 : 32,
  })),
});

const trendToPeriods = (trend: LoginTrendPoint[]): ActiveUsersPeriod[] =>
  trend.map((t) => ({
    period: t.date,
    CARE_SEEKER: t.CARE_SEEKER,
    FINANCIAL_ADVISOR: t.FINANCIAL_ADVISOR,
    SERVICE_PROVIDER: t.SERVICE_PROVIDER,
    ADMIN: t.ADMIN,
  }));

export const ActiveUsersBarChart = ({
  weeklyData,
  dailyData,
  dateRange,
}: ActiveUsersBarChartProps) => {
  const isDailyDisabled = DAILY_DISABLED_RANGES.includes(dateRange as DateRangePreset);
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');

  useEffect(() => {
    if (isDailyDisabled && viewMode === 'daily') setViewMode('weekly');
  }, [isDailyDisabled, viewMode]);

  const dailyPeriods = useMemo(() => trendToPeriods(dailyData), [dailyData]);
  const chartData = viewMode === 'weekly' ? weeklyData : dailyPeriods;

  if (chartData.length === 0) {
    return (
      <ChartContainer
        title="Active Users"
        subtitle="User activity breakdown by type"
      >
        <EmptyState message="No active user data available for the selected date range." />
      </ChartContainer>
    );
  }

  const toggle = (
    <div className="flex gap-1 rounded-md border border-gray-200 p-0.5">
      <Button
        variant={viewMode === 'daily' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 px-3 text-xs"
        disabled={isDailyDisabled}
        title={isDailyDisabled ? 'Daily view not available for 12-month range' : undefined}
        onClick={() => setViewMode('daily')}
      >
        Daily
      </Button>
      <Button
        variant={viewMode === 'weekly' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 px-3 text-xs"
        onClick={() => setViewMode('weekly')}
      >
        Weekly
      </Button>
    </div>
  );

  return (
    <ChartContainer
      title="Active Users"
      subtitle="User activity breakdown by type"
      action={toggle}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={buildChartOptions(chartData, viewMode)}
        style={{ height: 320 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
